import { useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { recordAppOpen, useAppShares, type WorkspaceApp } from '../../../lib/app-shares';
import { inBucket, RECENCY_BUCKETS, relativeOpenedAt, resumeApp, type RecencyBucket } from '../../../lib/app-recency';
import { ownsApp } from '../../../lib/app-ownership';
import { Badge, Button, Card, OverflowMenu, Typography } from '../ui';
import { AppsGrid, firstName } from './AppsGrid';
import { ShareAppDialog } from './ShareAppDialog';
import { DeleteAppDialog } from './DeleteAppDialog';
import styles from './RecencyBands.module.css';

// The Apps screen's stream: the app you were last in, then everything else in named time bands.
//
// **Why bands and not one long sorted list.** A bare recency sort reorders itself silently — the list a
// person learned yesterday is a different list today, with nothing on screen accounting for the change.
// Named bands keep the order legible, and they give "not opened yet" somewhere honest to sit: a
// just-built app is the most interesting thing on the screen, and a pure recency sort buries it last
// behind every app the reader has ever opened once.
//
// **Space is not structure here.** It is a quiet line on the card. Grouping by Space would make scope
// the organising principle and collide with recency — the filter narrows, the bands order, and the two
// stay orthogonal so a Space-narrowed list still answers "where was I?" within that Space.

/** The band titles, in render order. Named here so the screen cannot invent a different vocabulary. */
const BAND_KEY: Record<RecencyBucket, string> = {
  today: 'apps.band.today',
  week: 'apps.band.week',
  earlier: 'apps.band.earlier',
};

export function RecencyBands({
  apps,
  spaceNames,
  onOpenBi,
  onDeleted,
  onOpened,
}: {
  apps: WorkspaceApp[];
  /** Space id -> name, for the card and hero meta lines. The app records only the id. */
  spaceNames?: Record<string, string>;
  onOpenBi?: (appId: string) => void;
  onDeleted?: (appId: string) => void;
  onOpened?: (appId: string) => void;
}) {
  const { t } = useTranslation();
  // ONE `now` for the whole stream. Reading the clock per band would let an app fall between two of
  // them, or into both, if a boundary passed mid-render.
  const now = new Date();

  // The hero's own Share/Delete. The hero app is filtered OUT of the bands below, so without these it
  // is the one app on the screen that cannot be shared or deleted at all — and a Space holding a single
  // app renders only the hero, leaving that Space with no app management whatsoever. The dialogs live
  // here rather than in the hero so the gate and the dialogs stay in one place.
  const [sharing, setSharing] = useState<WorkspaceApp | null>(null);
  const [deleting, setDeleting] = useState<WorkspaceApp | null>(null);

  const resume = resumeApp(apps);
  // The hero's app is NOT repeated in the bands below it. Seeing the same card twice reads as a bug,
  // and the hero is a bigger rendering of that one app rather than a summary of a different thing.
  const rest = resume ? apps.filter((app) => app.id !== resume.id) : apps;
  // Which bands actually have content, computed ONCE: the render needs both the contents and how many
  // bands there are, and re-deriving that per band would let the two answers disagree.
  const filled = RECENCY_BUCKETS.map((bucket) => ({ bucket, band: inBucket(rest, bucket, now) }))
    .filter(({ band }) => band.length > 0);

  return (
    <div className={styles.stream}>
      {resume && (
        <ResumeHero
          app={resume}
          now={now}
          onOpenBi={onOpenBi}
          onOpened={onOpened}
          spaceName={resume.workspaceId ? spaceNames?.[resume.workspaceId] : undefined}
          onShare={setSharing}
          onDelete={setDeleting}
        />
      )}

      {filled.map(({ bucket, band }) => (
          <section key={bucket} className={styles.band}>
            {/* A LONE band draws no heading. A label exists to tell groups apart, so with one group
                there is nothing to tell apart — and the heading then spans the whole screen, where
                "Not opened yet" reads as a fault rather than a label. That is not an edge case: the
                recency map starts empty, so it is what EVERY account sees until it opens something.
                An empty band is likewise not drawn — a heading over nothing asks why it is empty. */}
            {filled.length > 1 && (
            <div className={styles.bandHead}>
              <Typography variant="p3" color="secondary" component="h3" className={styles.bandTitle}>
                {t(BAND_KEY[bucket])}
              </Typography>
              <span className={styles.rule} aria-hidden="true" />
              <Typography variant="p3" color="muted" component="span">
                {band.length}
              </Typography>
            </div>
            )}
            <AppsGrid apps={band} spaceNames={spaceNames} onOpenBi={onOpenBi} onDeleted={onDeleted} onOpened={onOpened} />
          </section>
      ))}
      {sharing && <ShareAppDialog app={sharing} onClose={() => setSharing(null)} />}
      {deleting && (
        <DeleteAppDialog
          app={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            onDeleted?.(deleting.id);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * The app the reader was last in, given its own card.
 *
 * <p>It earns the size because it is the one card most likely to be the reason they came: the whole
 * screen is ordered by "where was I?", and this is the answer. Absent entirely when nothing has ever
 * been opened — a "where you left off" over an app they have never seen would be a false sentence.
 */
function ResumeHero({
  app,
  now,
  spaceName,
  onOpenBi,
  onOpened,
  onShare,
  onDelete,
}: {
  app: WorkspaceApp;
  now: Date;
  spaceName?: string;
  onOpenBi?: (appId: string) => void;
  onOpened?: (appId: string) => void;
  onShare: (app: WorkspaceApp) => void;
  onDelete: (app: WorkspaceApp) => void;
}) {
  const { t } = useTranslation();
  const when = relativeOpenedAt(app.lastOpenedAt, now);
  /**
   * How many people this app is shared with, or undefined when we do not know.
   *
   * ONE request, for the ONE app the hero draws — not per card. `useAppShares` is the same read the
   * share dialog performs, and it is asked here only for an app the reader owns (see the render).
   * `undefined` while loading or on failure is deliberate: the pill is absent rather than showing a
   * number nobody can stand behind.
   */
  const isMine = ownsApp(app.workspaceId);
  const sharesState = useAppShares(isMine ? app.id : undefined);
  // Gated TWICE, deliberately: `undefined` stops the request, and `isMine` stops the RENDER. Relying on
  // the disabled query to return nothing makes the privacy rule a property of react-query's behaviour
  // rather than of this component — one `keepPreviousData` or a warm cache entry away from showing a
  // count about somebody else's grants. Both native surfaces gate in the render too
  // (`AppOwnership.isMine` / `resumeIsMine`); this now matches them.
  const sharedWithCount = isMine && sharesState.status === 'ready' ? sharesState.shares.length : undefined;

  const open = () => {
    if (!app.url) return;
    onOpenBi?.(app.id);
    // Same contract as the grid's card: fire the stamp, never wait on it, and open the tab with
    // `noopener` because the app is third-party code that would otherwise get a handle on this window.
    void recordAppOpen(app.id).then(() => onOpened?.(app.id));
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card size="lg" className={styles.hero}>
      <div className={styles.heroBody}>
        <Typography variant="p3" color="muted" component="p" className={styles.heroLabel}>
          {t('apps.resume.label')}
        </Typography>
        <Typography variant="h2" color="strong" component="h3">
          {app.name}
        </Typography>
        {app.description && (
          <Typography variant="p2" color="secondary" component="p" className={styles.heroDescription}>
            {app.description}
          </Typography>
        )}
        <div className={styles.heroMeta}>
          {when && (
            <Typography variant="p3" color="muted" component="span">
              {t('apps.card.opened', { when })}
            </Typography>
          )}
          {/* Whose app, when it is not the reader's — the same rule and the same pill the card uses,
              including that an unresolved name still says the app is not theirs. A shared app shows no
              Space, because it sits in none of the reader's. */}
          {!ownsApp(app.workspaceId) ? (
            <Badge
              variant="waiting-on-a-reply"
              label={
                app.ownerName?.trim()
                  ? t('apps.card.ownersApp', { name: firstName(app.ownerName) })
                  : t('apps.card.sharedWithYou')
              }
            />
          ) : spaceName ? (
            <span className={styles.metaGroup}>
              {when && <span className={styles.dot} aria-hidden="true" />}
              <Typography variant="p3" color="muted" component="span">
                {spaceName}
              </Typography>
            </span>
          ) : null}
          {/* WHO ELSE can see it — the one fact on this screen a reader cannot get anywhere else
              without opening the share dialog on every app in turn.
              Only on the reader's OWN app: on somebody else's you are a grantee, and the other
              grantees are not yours to enumerate (the API answers for the owner, so asking would
              show a failed read).
              Only when the read SUCCEEDED and found somebody: a share list is a permission list, so
              "we could not find out" must never render as a number, and an app shared with nobody
              says nothing rather than "Shared with 0 people". */}
          {sharedWithCount !== undefined && sharedWithCount > 0 && (
            <span className={styles.metaGroup}>
              <span className={styles.dot} aria-hidden="true" />
              {/* The DS grey chip, not the mock's pink one: the only pink Style in the badge set is
                  `paused`, whose ink is the error red — a red pill beside an app's name reads as a
                  warning about the app. Deliberate, written-down deviation from the mock's colour, and
                  it costs no clarity: this pill and the owner pill can never appear together (one is on
                  YOUR app, the other on somebody else's), so they cannot be confused for each other. */}
              <Badge
                variant="waiting-on-a-reply"
                label={
                  // The keys the SHARE SHEET already uses, not a new pair. I added
                  // `apps.card.sharedWith*` and a review caught it: `apps.share.sharedWith.one/.other`
                  // says exactly this, is already translated into all 37, and phrases the singular
                  // correctly — my Hebrew read "shared with person 1", a numeral after the noun, which
                  // renders as an ID rather than a count. One fact, one sentence, everywhere it appears.
                  sharedWithCount === 1
                    ? t('apps.share.sharedWith.one')
                    : t('apps.share.sharedWith.other', { count: sharedWithCount })
                }
              />
            </span>
          )}
        </div>
      </div>
      <div className={styles.heroActions}>
        {/* A real button, not the whole card: the hero carries more than one thing to read, and a card
            that opens an app the moment a reader clicks its description is a trap. */}
        {app.url && (
          <Button variant="contained" color="accent" size="medium" onClick={open}>
            {t('apps.resume.open')}
          </Button>
        )}
        {/* Same ownership gate the card uses: a shared app offers neither, and draws no trigger. */}
        {ownsApp(app.workspaceId) && (
          <OverflowMenu
            ariaLabel={t('apps.rowMenu.label', { name: app.name })}
            actions={[
              { key: 'share', label: t('apps.share.action'), onSelect: () => onShare(app) },
              { key: 'delete', label: t('apps.delete.action'), destructive: true, onSelect: () => onDelete(app) },
            ]}
          />
        )}
      </div>
    </Card>
  );
}
